import React from 'react';

import {Icon} from '@edna/components/primitives';

import {isEnvDevelopment, isProdDomain} from 'src/constants';
import {useGetBalanceQuery} from 'src/containers/Billing';
import {
  EResellerIds,
  ERoles,
  useGetCurrentUserQuery,
  useGetTenantDataQuery,
  useRole,
} from 'src/containers/User';
import {SmsIcon} from 'src/icons';
import {sendMetrics} from 'src/utils';

import * as SS from '../style';
import {
  EDNA_PULSE_MFM_SCRIPT,
  EDNA_PULSE_NOT_D_SCRIPT,
  EDNA_PULSE_SCRIPT,
  TEST_EP_NOT_D_SCRIPT,
  TEST_EP_SCRIPT,
} from './definitions';

const PERSONAL_MANAGER_IDS: Partial<Record<EResellerIds, number[]>> = {
  [EResellerIds.OSK_ID]: [2454, 4882],
  [EResellerIds.KZ_ID]: [856],
  [EResellerIds.MFM_ID]: [902],
};

const useChatCenterWidgetSourceCode = () => {
  const isAgent = useRole(ERoles.ROLE_AGENT);
  const {data: tenant} = useGetTenantDataQuery();

  // в localhost спамит ошибка
  // "[Violation] Permissions policy violation: unload is not allowed in this document."
  if (isEnvDevelopment) {
    return;
  }

  if (isAgent || !tenant?.personalManagerId) {
    return;
  }

  const isSpecialManagerId = (PERSONAL_MANAGER_IDS[tenant.resellerId] ?? []).includes(
    tenant.personalManagerId,
  );
  const resellerId = tenant.resellerId;

  if (resellerId === EResellerIds.MFM_ID && isSpecialManagerId) {
    return isProdDomain() ? EDNA_PULSE_MFM_SCRIPT : TEST_EP_NOT_D_SCRIPT;
  }

  if ([EResellerIds.OSK_ID, EResellerIds.KZ_ID].includes(resellerId) && isSpecialManagerId) {
    return isProdDomain() ? EDNA_PULSE_SCRIPT : TEST_EP_SCRIPT;
  }

  if ([EResellerIds.OSK_ID, EResellerIds.KZ_ID].includes(resellerId) && !isSpecialManagerId) {
    return isProdDomain() ? EDNA_PULSE_NOT_D_SCRIPT : TEST_EP_NOT_D_SCRIPT;
  }

  return undefined;
};

const ChatCenterWidget = React.memo<{scriptCode: string}>(({scriptCode}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const {data: user} = useGetCurrentUserQuery();
  const {data: tenant} = useGetTenantDataQuery();
  const {data: balance} = useGetBalanceQuery();

  React.useEffect(() => {
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.innerHTML = scriptCode;

    containerRef?.current?.appendChild(script);
  }, []);

  React.useEffect(() => {
    window.clientId = user?.login;
    window.clientData = JSON.stringify({
      tenantID: tenant?.tenantId,
      balance: balance?.amount,
      locked: tenant?.locked,
      temporaryBlocked: tenant?.temporaryBlocked,
      manager: tenant?.personalManagerName,
      reseller: tenant?.resellerId,
    });
  }, [user, tenant, balance]);

  return <div ref={containerRef}></div>;
});

const showChatCenterWidget = () => {
  sendMetrics('widget-button-pressed');

  window.ThreadsWidget?.showChat();
};

const ChatCenterWidgetButton = React.memo(() => (
  <SS.WidgetButton isFixed onClick={showChatCenterWidget}>
    <Icon size="32px" as={SmsIcon} />
  </SS.WidgetButton>
));

ChatCenterWidget.displayName = 'ChatCenterWidget';

export {
  ChatCenterWidget,
  ChatCenterWidgetButton,
  useChatCenterWidgetSourceCode,
  showChatCenterWidget,
};
